import { generateLevel, verifyLevels } from './generator';
import { createGameState, tryMoveArrow, finishAnimation } from './game';
import { computeLayout, renderFrame, renderOverlay, resetConfetti } from './renderer';
import { pickArrow } from './input';
import { t } from './i18n';
import { track } from './analytics';
import type { GameState } from './types';

// ── Canvas setup ────────────────────────────────────────────────────────────
const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;

function resize() {
  const dpr = window.devicePixelRatio || 1;
  canvas.width  = window.innerWidth  * dpr;
  canvas.height = window.innerHeight * dpr;
  canvas.style.width  = window.innerWidth  + 'px';
  canvas.style.height = window.innerHeight + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

new ResizeObserver(resize).observe(document.body);
window.visualViewport?.addEventListener('resize', resize);
resize();

// ── Level / state ────────────────────────────────────────────────────────────
const LEVEL_STORAGE_KEY = 'game-arrows-level';

let levelIndex = Math.max(0, parseInt(localStorage.getItem(LEVEL_STORAGE_KEY) ?? '0', 10) || 0);
let level = generateLevel(levelIndex + 1);
let state: GameState = createGameState(level);

// ── Analytics (события раунда) ────────────────────────────────────────────────
let ended = false; // терминал (level_complete/game_over) шлём один раз на раунд
function reportStart() {
  ended = false;
  track('game_start');
}

function loadLevel(idx: number) {
  levelIndex = idx;
  level = generateLevel(levelIndex + 1);
  state = createGameState(level);
  resetConfetti();
  localStorage.setItem(LEVEL_STORAGE_KEY, String(levelIndex));
  reportStart();
}

reportStart(); // первый раунд (boot)

// ── Game loop ────────────────────────────────────────────────────────────────
function loop(now: number) {
  const dpr = window.devicePixelRatio || 1;
  const viewW = canvas.width / dpr;
  const viewH = canvas.height / dpr;
  const layout = computeLayout(viewW, viewH, level.cols, level.rows);

  // Resolve completed animations
  if (state.animating) {
    const elapsed = now - state.animating.startTime;
    if (elapsed >= state.animating.duration) {
      state = finishAnimation(state);
      if (state.status !== 'playing') {
        menuOpen = false;
        if (!ended) {
          ended = true;
          if (state.status === 'won') track('level_complete', { level: levelIndex + 1 });
          else track('game_over', { result: 'lose', level: levelIndex + 1 });
        }
      }
    }
  }

  renderFrame(ctx, viewW, viewH, level, state.arrows, layout, state.animating, now, state.erroredArrows);
  renderOverlay(ctx, viewW, viewH, state.status, state.lives);
  drawLevelNav(ctx, viewW, viewH);
  drawMenu(ctx, viewW, viewH);

  requestAnimationFrame(loop);
}

requestAnimationFrame(loop);

// ── Input ────────────────────────────────────────────────────────────────────
function handleTap(screenX: number, screenY: number, now: number) {
  const dprM = window.devicePixelRatio || 1;
  const vW = canvas.width / dprM;
  const vH = canvas.height / dprM;
  const m = menuRects(vW, vH);

  if (menuOpen) {
    if (inRect(screenX, screenY, m.resume))  { menuOpen = false; return; }
    if (inRect(screenX, screenY, m.restart)) { menuOpen = false; loadLevel(levelIndex); return; }
    if (inRect(screenX, screenY, m.hub))     { location.href = document.body.dataset.hubUrl ?? '/'; return; }
    menuOpen = false; return;   // тап мимо пунктов — закрыть
  }
  if (state.status === 'playing' &&
      Math.hypot(screenX - m.toggle.cx, screenY - m.toggle.cy) <= m.toggle.r + 6) {
    menuOpen = true; return;
  }

  if (state.status === 'won') {
    loadLevel(levelIndex + 1);
    return;
  }
  if (state.status === 'lost') {
    loadLevel(levelIndex);
    return;
  }
  if (state.animating) return;

  const dpr = window.devicePixelRatio || 1;
  const viewW = canvas.width / dpr;
  const viewH = canvas.height / dpr;
  const layout = computeLayout(viewW, viewH, level.cols, level.rows);

  const navBtn = hitLevelNav(screenX, screenY, viewW, viewH);
  if (navBtn === 'prev') { loadLevel(levelIndex - 1); return; }
  if (navBtn === 'next') { loadLevel(levelIndex + 1); return; }

  const threshold = Math.max(layout.cellSize * 0.85, 18);
  const arrow = pickArrow(screenX, screenY, state.arrows, layout, threshold);
  if (arrow) {
    state = tryMoveArrow(state, arrow, level, now);
  }
}

canvas.addEventListener('touchstart', e => {
  e.preventDefault();
  const t = e.changedTouches[0];
  const rect = canvas.getBoundingClientRect();
  handleTap(t.clientX - rect.left, t.clientY - rect.top, performance.now());
}, { passive: false });

canvas.addEventListener('click', e => {
  const rect = canvas.getBoundingClientRect();
  handleTap(e.clientX - rect.left, e.clientY - rect.top, performance.now());
});

// ── Level nav UI ─────────────────────────────────────────────────────────────
const NAV_SIZE = 44;
const NAV_MARGIN = 14;

function drawLevelNav(ctx: CanvasRenderingContext2D, viewW: number, viewH: number) {
  const y = viewH - NAV_MARGIN - NAV_SIZE / 2;
  ctx.save();
  ctx.font = '13px system-ui, sans-serif';
  ctx.fillStyle = '#888';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(t('level', { n: levelIndex + 1 }), viewW / 2, y);

  if (levelIndex > 0) drawNavBtn(ctx, NAV_MARGIN + NAV_SIZE / 2, y, '‹');
  drawNavBtn(ctx, viewW - NAV_MARGIN - NAV_SIZE / 2, y, '›');
  ctx.restore();
}

function drawNavBtn(ctx: CanvasRenderingContext2D, x: number, y: number, label: string) {
  ctx.fillStyle = 'rgba(0,0,0,0.07)';
  ctx.beginPath();
  ctx.arc(x, y, NAV_SIZE / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#555';
  ctx.font = 'bold 22px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x, y + 1);
}

function hitLevelNav(x: number, y: number, viewW: number, viewH: number): 'prev' | 'next' | null {
  const cy = viewH - NAV_MARGIN - NAV_SIZE / 2;
  const r = NAV_SIZE / 2 + 8; // larger hit area

  if (levelIndex > 0 && Math.hypot(x - (NAV_MARGIN + NAV_SIZE / 2), y - cy) <= r) return 'prev';
  if (Math.hypot(x - (viewW - NAV_MARGIN - NAV_SIZE / 2), y - cy) <= r) return 'next';
  return null;
}

// ── Menu (☰ → overlay: Resume / Restart / Home) ──────────────────────────────
type Rect = { x: number; y: number; w: number; h: number };
type Circle = { cx: number; cy: number; r: number };

let menuOpen = false;

const MENU_TOGGLE = 40;   // диаметр ☰-кнопки
const MENU_EDGE   = 14;   // отступ от края

function menuRects(viewW: number, viewH: number) {
  const toggle: Circle = { cx: MENU_EDGE + MENU_TOGGLE / 2, cy: MENU_EDGE + MENU_TOGGLE / 2, r: MENU_TOGGLE / 2 };
  const bw = Math.min(260, viewW - 64);
  const bh = 52, gap = 14;
  const px = (viewW - bw) / 2;
  const total = bh * 3 + gap * 2;
  const py = (viewH - total) / 2;
  const resume:  Rect = { x: px, y: py,                 w: bw, h: bh };
  const restart: Rect = { x: px, y: py + (bh + gap),    w: bw, h: bh };
  const hub:     Rect = { x: px, y: py + (bh + gap) * 2, w: bw, h: bh };
  const panel:   Rect = { x: px - 20, y: py - 24, w: bw + 40, h: total + 48 };
  return { toggle, panel, resume, restart, hub };
}

function inRect(x: number, y: number, r: Rect): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

function drawMenu(ctx: CanvasRenderingContext2D, viewW: number, viewH: number) {
  if (state.status !== 'playing') return;   // ☰ только во время игры
  const { toggle, panel, resume, restart, hub } = menuRects(viewW, viewH);

  // ☰-кнопка
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.07)';
  ctx.beginPath();
  ctx.arc(toggle.cx, toggle.cy, toggle.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#555';
  ctx.font = 'bold 20px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('☰', toggle.cx, toggle.cy + 1);
  ctx.restore();

  if (!menuOpen) return;

  // затемнение + панель
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(0, 0, viewW, viewH);
  ctx.fillStyle = '#fff';
  roundRectPath(ctx, panel.x, panel.y, panel.w, panel.h, 18);
  ctx.fill();

  const items: [Rect, string, boolean][] = [
    [resume,  t('resume'),       false],
    [restart, t('restart_game'), false],
    [hub,     t('to_hub'),       true],   // акцентный
  ];
  for (const [r, label, accent] of items) {
    ctx.fillStyle = accent ? '#111' : 'rgba(0,0,0,0.06)';
    roundRectPath(ctx, r.x, r.y, r.w, r.h, 12);
    ctx.fill();
    ctx.fillStyle = accent ? '#fff' : '#111';
    ctx.font = 'bold 17px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  }
  ctx.restore();
}

// ── Dev-хук: ?verify=N — прогон генератора солвером, в проде не исполняется ──
const verifyN = parseInt(new URLSearchParams(location.search).get('verify') ?? '', 10);
if (verifyN > 0) {
  const reports = verifyLevels(verifyN);
  const bad = reports.filter(r => !r.solved);
  if (bad.length) console.error('UNSOLVABLE LEVELS:', bad);
  else console.log(`verify: all ${reports.length} levels solvable`);
  console.table(reports.filter(r => r.level <= 30 || r.level % 50 === 0));
}
